import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  createClass,
  getClasses
} from "../services/APIClient";
import "./TeacherCreateClassModal.css";

const CLASS_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" }
];

const normaliseName = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getClassLevelValue = (classLevel) => {
  const normalisedClassLevel = normaliseName(classLevel);

  return (
    CLASS_LEVELS.find(
      (level) =>
        level.value === classLevel ||
        normaliseName(level.label) === normalisedClassLevel
    )?.value || ""
  );
};

const normalisePrefill = (prefill) => ({
  className: String(prefill?.className || prefill?.class_name || "")
    .trim()
    .replace(/\s+/g, " "),
  classLevel: getClassLevelValue(prefill?.classLevel || prefill?.class_level)
});

const getCreatedClassFromResponse = (response, fallbackClass) => {
  const possibleClasses = [
    ...(Array.isArray(response) ? response : []),
    ...(Array.isArray(response?.classes) ? response.classes : []),
    ...(Array.isArray(response?.createdClasses) ? response.createdClasses : []),
    ...(Array.isArray(response?.data) ? response.data : [])
  ];
  const possibleClass =
    possibleClasses[0] ||
    response?.class ||
    response?.createdClass ||
    response;

  if (possibleClass && typeof possibleClass === "object") {
    return {
      ...fallbackClass,
      ...possibleClass,
      id: possibleClass.id || possibleClass._id || fallbackClass.id,
      name: possibleClass.name || fallbackClass.name
    };
  }

  return fallbackClass;
};

function TeacherCreateClassModal({
  show,
  handleClose,
  teacherid,
  prefill,
  onAddChildren,
  embedded = false,
  onWorkflowComplete
}) {
  const initialPrefill = normalisePrefill(prefill);
  const [stage, setStage] = useState("form");
  const [className, setClassName] = useState(initialPrefill.className);
  const [classLevel, setClassLevel] = useState(initialPrefill.classLevel);
  const [existingClasses, setExistingClasses] = useState([]);
  const [createdClass, setCreatedClass] = useState(null);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [classNameTouched, setClassNameTouched] = useState(false);
  const [classLevelTouched, setClassLevelTouched] = useState(false);
  const [error, setError] = useState("");

  const trimmedClassName = className.trim().replace(/\s+/g, " ");
  const selectedClassLevel = CLASS_LEVELS.find((level) => level.value === classLevel);
  const duplicateClass = useMemo(
    () =>
      Boolean(trimmedClassName) &&
      existingClasses.some((classItem) =>
        normaliseName(classItem?.name) === normaliseName(trimmedClassName)
      ),
    [existingClasses, trimmedClassName]
  );
  const canCreateClass =
    Boolean(trimmedClassName && selectedClassLevel && !duplicateClass && !isLoadingClasses && !isCreating);

  useEffect(() => {
    if (!show) {
      return;
    }

    const nextPrefill = normalisePrefill(prefill);

    setStage("form");
    setClassName(nextPrefill.className);
    setClassLevel(nextPrefill.classLevel);
    setExistingClasses([]);
    setCreatedClass(null);
    setError("");
    setClassNameTouched(false);
    setClassLevelTouched(false);
    setIsLoadingClasses(true);

    getClasses()
      .then((classesResponse) => {
        setExistingClasses(Array.isArray(classesResponse) ? classesResponse : []);
      })
      .catch((classesError) => {
        console.error("Error loading teacher classes:", classesError);
        setExistingClasses([]);
        setError("Existing classes could not be checked. You can still create a class.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, prefill]);

  const handleCreateClass = async (event) => {
    event.preventDefault();
    setClassNameTouched(true);
    setClassLevelTouched(true);

    if (!canCreateClass) {
      return;
    }

    setIsCreating(true);
    setError("");

    const fallbackClass = {
      name: trimmedClassName,
      grade: selectedClassLevel.label,
      [selectedClassLevel.value]: true
    };

    try {
      const response = await createClass(
        trimmedClassName,
        selectedClassLevel.label,
        selectedClassLevel.value === "grade_level1",
        selectedClassLevel.value === "grade_level2",
        false,
        false,
        teacherid
      );
      const newClass = getCreatedClassFromResponse(response, fallbackClass);

      setCreatedClass(newClass);
      setStage("success");
    } catch (createError) {
      console.error("Error creating teacher class:", createError);
      setError("The class could not be created. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddChildren = () => {
    if (onAddChildren) {
      onAddChildren(createdClass);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: createdClass?.id || "",
        message: "Class created."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderForm = () => (
    <Form onSubmit={handleCreateClass} noValidate>
      <Modal.Header closeButton>
        <Modal.Title>Create Class</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="create-class-form-grid">
          <Form.Group controlId="teacherCreateClassName">
            <Form.Label>Class name</Form.Label>
            <Form.Control
              type="text"
              value={className}
              onBlur={() => setClassNameTouched(true)}
              onChange={(event) => {
                setClassName(event.target.value);
                setError("");
              }}
              placeholder="Blue Room"
              aria-label="Class name"
              isInvalid={
                (classNameTouched && !trimmedClassName) ||
                duplicateClass
              }
            />
            {classNameTouched && !trimmedClassName && (
              <Form.Control.Feedback type="invalid">
                Enter a class name.
              </Form.Control.Feedback>
            )}
            {duplicateClass && (
              <Form.Control.Feedback type="invalid">
                A class with this name already exists.
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group controlId="teacherCreateClassLevel">
            <Form.Label>Class level</Form.Label>
            <Form.Select
              value={classLevel}
              onBlur={() => setClassLevelTouched(true)}
              onChange={(event) => {
                setClassLevel(event.target.value);
                setError("");
              }}
              aria-label="Class level"
              isInvalid={classLevelTouched && !selectedClassLevel}
            >
              <option value="">Select class level</option>
              {CLASS_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </Form.Select>
            {classLevelTouched && !selectedClassLevel && (
              <Form.Control.Feedback type="invalid">
                Choose a class level.
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={!canCreateClass}>
          {isCreating ? "Creating..." : "Create class"}
        </Button>
      </Modal.Footer>
    </Form>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Class Ready</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="success" className="create-class-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{createdClass?.name || trimmedClassName} is ready.</strong>
          </span>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleAddChildren}>
          Add children
        </Button>
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-create-class-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherCreateClassModal;
